import { useCallback, useEffect, useRef } from 'react'
import { Container, Box, Group, Title, Text } from '@mantine/core'
import './_chart.css'
import Seo from '../../_Seo'
import LazyMap from '../../stations/_LazyMap'
import FavoriteButton from '../../stations/_FavoriteButton'
import { useStations } from '../../_stations'
import { useI18n } from '../../_i18n'
import { viewFromBbox, frameBbox } from '../_chart'
import { trackChartError } from '../../_analytics'

// One chart area: title, the chart's water, and the paragraph explaining what
// is on it. No station or related-chart lists — the stations render ON the
// map (chartMode), and a reader here is looking at this water, not browsing.

// `chart` is the /api/v2/charts/:id payload, verbatim — the page is a client
// of the same API the native app reads, and adds nothing to it. `analytics` is
// the web-only pair the controller composes beside it.
export default function ChartPage({
  chart,
  analytics = null,
  layer_coverage: layerCoverage = null,
}) {
  const { t } = useI18n()
  // What the map opens on, and then fits to: the chart's main panel where the
  // catalog tagged one, its whole extent otherwise (`?frame=union` forces the
  // latter for comparison). Both the first frame and the on-load fit read the
  // same box, or the fit would pull the camera straight back to the union.
  const frame = frameBbox(chart)

  const pageName = t('chart_page_name', { title: chart.title })

  // Record this chart as a recent on visit, and re-stamp its slug wherever it
  // is already stored. The star and the lists key on `id` — the database id,
  // which never moves — while `slug` is the link, and a rename (29 of them on
  // 2026-09-04) is only ever noticed here, on the page that knows the current
  // one. Bare title, not the localized `pageName`: a stored entry outlives the
  // language the reader happened to be in when they saved it.
  const item = { id: chart.id, name: chart.title, slug: chart.permalink }
  const { recordRecent } = useStations()
  useEffect(() => {
    if (chart.id != null && chart.title) recordRecent('chart', item)
  }, [chart.id, chart.permalink])

  // The one failure the map cannot report about itself: its own module never
  // arrived, so nothing inside ExploreMap ever ran. Terminal by construction —
  // LazyMap does not retry the import — which is why this is the one Chart Error
  // sent without waiting to see whether the chart recovers.
  //
  // No overlap with the map's own error boundary: a failed import means
  // ExploreMap never mounted, so the two can never both fire for one visit.
  const reportedLoadFailure = useRef(false)
  const onMapUnavailable = useCallback(() => {
    if (reportedLoadFailure.current) return
    reportedLoadFailure.current = true
    trackChartError(analytics, 'map_init')
  }, [analytics])

  return (
    <>
      {/* The page NAME is composed here, per locale, from the chart's own
          English name — "{{title}} Nautical Chart" in en, its native equivalent
          in the other eleven. It used to be concatenated in Ruby, which shipped
          the English words to every locale, and skipped entirely when the title
          already contained "chart" — a guard that means nothing in ja/ko/zh and
          is what left Randy's page without the wrapper at all. The chart's NAME
          stays English: it is a US proper noun, and this site does not build
          bilingual strings. */}
      <Seo title={pageName} description={t('chart_seo_description', { title: chart.title })} />
      <Container size="lg" py="xl" className="chartLayout">
        {/* No breadcrumb: there is no chart index to climb to. The star sits
            beside the H1 exactly as it does on a station page — same component,
            same gold, same place — because Randy asked for charts to work "the
            exact same way" as tides and currents. */}
        <Group gap="xs" wrap="nowrap" align="center" mb="md">
          <Title order={1} className="chartTitle">
            {pageName}
          </Title>
          <FavoriteButton kind="chart" item={item} />
        </Group>

        {/* The page's only prose, and it earns the space twice over. For a
            reader it answers the question the page never answered: what this
            is, and what it costs. For a search engine it is something to quote.
            With no paragraph anywhere on the page, Google was building result
            snippets out of the skip link and the basemap attribution ("Skip to
            main content. Tides Near Me. ... OpenFreeMap (c) OpenMapTiles"),
            which is what it does when a page gives it nothing better.
            Templated on the chart title so ~900 pages are not one paragraph
            repeated: near-duplicate boilerplate across a large URL set is
            worth less than no text at all.

            On phones this paragraph renders BELOW the map (flex order in
            _chart.css) while staying right here in the DOM — the map is the
            page's subject and used to open half below the fold. */}
        <Text mb="lg" size="lg" className="chartIntro">
          {t('chart_intro', { title: chart.title })}
        </Text>

        {/* The chart's water, over the depth stack. Above the fold, so it loads
            immediately rather than waiting on the scroll observer.
            The chart's frame sets the view and is then left undrawn: an
            outline over the depths says nothing a reader needs, and a box drawn
            across the water invites the question of how exact it is.

            `chartMode` is what makes this a chart rather than /explore parked
            on depth: no layer toggle, no interpolated tide/current field, and
            the live tide and current stations drawn ON the chart — this page
            has no station list, so the map is the only place they appear.

            `analytics` is the bounded { chart_id, catalog } pair the map's
            Plausible events carry, straight from the controller — nothing is
            derived from the URL here, because the slug moves and the locale
            prefix comes and goes. It is also the switch: no other map on the
            site is passed one, so no other map emits these events. */}
        {/* Geometry lives in _chart.css (desktop min(560px,60vh); phones go
            full-bleed at min(600px,68svh)) so the phone override cascades
            cleanly — and the CONTAINERS table in ../_chart.js is the camera's
            assumption of these sizes: change one, change both. */}
        <Box className="chartMapBox">
          <LazyMap
            load={() => import('../../explore/ExploreMap')}
            surface="chart"
            height="100%"
            syncUrl={false}
            initialView={viewFromBbox(frame)}
            initialBounds={frame}
            initialMode="depth"
            chartMode
            // THE GO CONTROL, and the surface it reports (plan section 2.3:
            // one button, the same slot, every map surface). A chart page is
            // expected to be the HIGHEST-VALUE door into Go Mode even though
            // home is the most common one — a new reader with a boat under
            // them — which is why the event carries which door was used.
            //
            // IT IS ALSO THIS PAGE'S LOCATE CONTROL (plan section 2.2, ruling
            // 5.1). There is no second button for "where am I", because on a
            // page with no dot and no follow the honest answer to that question
            // is Go Mode at the reader's real location: it opens wherever they
            // are, with no coverage check and no refusal, and the browser's one
            // location prompt is raised on that gesture inside Go Mode's own
            // flow. "If we don't allow it here we need to tell the user how to
            // get there" — the button they reach for IS the way there.
            goEntry="chart"
            // NO DOT, NO FOLLOW, NO PROMPT ON LOAD (plan section 2.2). This
            // page used to carry `askLocation="maximize"`, which spent the
            // browser's single location prompt when a reader made the map full
            // screen — and drew a modal scrim over the whole control column
            // while it waited for an answer. The page is about a PLACE the
            // reader searched for and is usually not at: a dot 300 miles away
            // is noise, and a permission dialog on an SEO landing page costs
            // conversions. The ask moved to the gesture that is actually about
            // the reader, which is GO. `_askPolicy` has the rest, including
            // what was deleted with the mode.
            askLocation="never"
            layerCoverage={layerCoverage}
            expandInPlace
            analytics={analytics ?? null}
            onLoadFailure={onMapUnavailable}
          />
        </Box>
      </Container>
    </>
  )
}
